import React, { useState } from 'react';

import { Box, Card, Flex, Icon, Link, Text } from '@chakra-ui/react';
import { Fire } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';

import OrgRouteLink from 'components/common/OrgRouteLink';
import { useToastContext } from 'components/common/ToastBannerProvider/ToastBannerProvider';
import {
  useNotificationPolicy,
  useUserPolicyCompliance,
} from 'queries/notificationPolicy';
import {
  useSignalDestinations,
  useAllSignalNotifications,
  useCreateSignalsNotification,
  useUpdateSignalsNotification,
} from 'queries/signalsNotifications';

import { calculateComplianceChanges } from './helpers/complianceHelpers';
import { ComplianceWarning } from './NotificationPolicy/ComplianceWarning';
import { NotificationPolicyComplianceAlert } from './NotificationPolicy/NotificationPolicyComplianceAlert';
import PhoneVerificationModal from './PhoneVerificationModal';
import SignalsNotificationsForm from './SignalsNotificationForm';

const PhonePromptMessage = ({ refetchDestinations }) => {
  const [verifyNumber, setVerifyNumber] = useState();

  return (
    <>
      <PhoneVerificationModal
        refetch={refetchDestinations}
        reverify={false}
        setReverify={() => {}}
        verifyNumber={verifyNumber}
        setVerifyNumber={setVerifyNumber}
      />
    </>
  );
};

const SignalsNotificationSettings = ({
  readOnly = false,
  notificationPolicyComplianceStatus,
}) => {
  const { addToast } = useToastContext();
  const queryClient = useQueryClient();
  const { data: destinations, refetch } = useSignalDestinations();
  const { data: policy } = useNotificationPolicy();
  const { data: userPolicyCompliance, refetch: refetchUserPolicyCompliance } =
    useUserPolicyCompliance({
      enabled: !readOnly,
    });

  // Fetch all notifications for compliance checking
  const {
    data: allSignalNotifications,
    refetch: refetchAllSignalNotifications,
  } = useAllSignalNotifications();
  const { mutate: createNotification } = useCreateSignalsNotification();
  const { mutate: updateNotification } = useUpdateSignalsNotification();

  // State for loading
  const [isUpdatingCompliance, setIsUpdatingCompliance] = useState(false);

  // Use the appropriate compliance data based on readOnly mode
  const complianceData = readOnly
    ? notificationPolicyComplianceStatus
    : userPolicyCompliance;

  // Callback to refresh compliance data when notifications change
  const handleNotificationChange = async () => {
    try {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['signals_notification_settings'],
        }),
        refetchAllSignalNotifications(),
        refetchUserPolicyCompliance(),
      ]);
    } catch (error) {
      // Error handling can be added here if needed
    }
  };

  // Helper function to check if a policy item is compliant
  const isStepCompliantById = (stepId, complianceData) => {
    const compliance = complianceData?.find(
      (item) => item.notification_policy_item_id === stepId,
    );
    const isCompliant = compliance?.is_compliant ?? false;
    return isCompliant;
  };

  // Check if user has any non-compliant steps for a priority
  const hasPriorityNonCompliance = (policyPriority, complianceData) => {
    if (!policyPriority?.steps?.length || !complianceData) return false;

    const hasNonCompliance = policyPriority.steps.some(
      (step) => !isStepCompliantById(step.id, complianceData),
    );

    return hasNonCompliance;
  };

  const highPriorityPolicy = policy?.priorities?.find(
    (p) => p.priority === 'HIGH',
  );
  const medPriorityPolicy = policy?.priorities?.find(
    (p) => p.priority === 'MEDIUM',
  );
  const lowPriorityPolicy = policy?.priorities?.find(
    (p) => p.priority === 'LOW',
  );

  const hasHighNonCompliance = hasPriorityNonCompliance(
    highPriorityPolicy,
    complianceData,
  );
  const hasMedNonCompliance = hasPriorityNonCompliance(
    medPriorityPolicy,
    complianceData,
  );
  const hasLowNonCompliance = hasPriorityNonCompliance(
    lowPriorityPolicy,
    complianceData,
  );

  const hasAnyNonCompliance =
    hasHighNonCompliance || hasMedNonCompliance || hasLowNonCompliance;

  // Handler for one-click compliance across all priorities
  const handleMatchAndUpdateAll = async () => {
    if (!allSignalNotifications || !policy?.priorities || !destinations) {
      return;
    }

    setIsUpdatingCompliance(true);

    try {
      // Get all policy items from all priorities, enriching each step with priority info
      const allPolicyItems = policy.priorities.flatMap((priority) =>
        (priority.steps || []).map((step) => {
          // Extract the notification group method from the notification_type object
          const notificationGroupMethod =
            step.notification_group_method ||
            step.notification_type?.value ||
            step.notification_type?.notification_group_method ||
            step.notification_type?.notification_type ||
            step.notification_type?.type;

          return {
            ...step,
            priority: priority.priority,
            notification_group_method: notificationGroupMethod,
            max_delay: step.delay || step.max_delay,
          };
        }),
      );

      const { updates, creates, unsatisfiable } = calculateComplianceChanges(
        allSignalNotifications,
        allPolicyItems,
        destinations,
      );

      // Process updates and creates sequentially to avoid React Query consecutive mutations issue
      const operationResults = [];

      // Process updates sequentially
      for (let i = 0; i < updates.length; i++) {
        const updatedPreference = updates[i];
        const operationId = `Update ${i + 1}`;

        try {
          const mutationPromise = new Promise((resolve, reject) => {
            updateNotification(updatedPreference, {
              onSuccess: (result) => {
                resolve(result);
              },
              onError: (error) => {
                reject(error);
              },
            });
          });

          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => {
              reject(new Error(`${operationId} timeout after 30 seconds`));
            }, 30000);
          });

          const result = await Promise.race([mutationPromise, timeoutPromise]);
          operationResults.push({ success: true, result, operationId });
        } catch (error) {
          operationResults.push({ success: false, error, operationId });
        }
      }

      // Process creates sequentially
      for (let i = 0; i < creates.length; i++) {
        const newPreference = creates[i];
        const operationId = `Create ${i + 1}`;

        try {
          const mutationPromise = new Promise((resolve, reject) => {
            createNotification(newPreference, {
              onSuccess: (result) => {
                resolve(result);
              },
              onError: (error) => {
                reject(error);
              },
            });
          });

          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => {
              reject(new Error(`${operationId} timeout after 30 seconds`));
            }, 30000);
          });

          const result = await Promise.race([mutationPromise, timeoutPromise]);
          operationResults.push({ success: true, result, operationId });
        } catch (error) {
          operationResults.push({ success: false, error, operationId });
        }
      }

      // All operations completed sequentially
      // Categorize results based on the new format
      const successful = operationResults.filter(
        (result) => result.success === true,
      );
      const failed = operationResults.filter(
        (result) => result.success === false,
      );

      // Calculate success counts
      const successfulUpdates = successful.filter((r) =>
        r.operationId?.startsWith('Update'),
      ).length;
      const successfulCreates = successful.filter((r) =>
        r.operationId?.startsWith('Create'),
      ).length;
      const failedCount = failed.length;

      // Show appropriate message based on results
      const unsatisfiableCount = unsatisfiable?.length || 0;
      const hasUnsatisfiable = unsatisfiableCount > 0;
      const hasFailures = failedCount > 0;
      const hasSuccesses = successful.length > 0;

      if (hasFailures || hasUnsatisfiable) {
        let message = '';
        if (hasSuccesses) {
          message += `Updated ${successfulUpdates} preferences and created ${successfulCreates} new preferences. `;
        }
        if (hasFailures) {
          message += `${failedCount} operations failed. `;
        }
        if (hasUnsatisfiable) {
          const unsatisfiableReasons = unsatisfiable
            .map((item) => item.reason)
            .join(', ');
          message += `${unsatisfiableCount} policy requirements could not be satisfied: ${unsatisfiableReasons}.`;
        }

        addToast({
          message: message.trim(),
          variant: 'warning',
        });
      } else if (hasSuccesses) {
        addToast({
          message: `Updated ${successfulUpdates} preferences and created ${successfulCreates} new preferences for compliance across all priority levels.`,
          variant: 'success',
        });
      } else {
        addToast({
          message:
            'All notification preferences are already compliant with policies.',
          variant: 'info',
        });
      }

      // Refetch data after operations complete
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['signals_notification_settings'],
        }),
        refetchUserPolicyCompliance(),
      ]);
    } catch (error) {
      addToast({
        message:
          'An error occurred while updating preferences. Please try again.',
        variant: 'error',
      });
    } finally {
      setIsUpdatingCompliance(false);
    }
  };

  const noVerifiedPhone =
    destinations?.filter((destination) => destination.type === 'phone')
      .length === 0;
  return (
    <>
      <Flex flexDir="column" mb="4">
        <Text textStyle="heading2" mb="2">
          Alert Notifications
        </Text>
        <Text textStyle="body1" color="content.primary">
          Choose a notification type and time to configure how you are notified
          when an alert is assigned to you. Add phone numbers, additional
          emails, and link third-party accounts in the{' '}
          <Link to="/account/edit" as={OrgRouteLink}>
            Profile
          </Link>{' '}
          tab.
        </Text>
        {noVerifiedPhone && !readOnly && (
          <PhonePromptMessage refetchDestinations={refetch} />
        )}

        {/* Show compliance alert if policy exists and user is not compliant */}
        {policy && hasAnyNonCompliance && !readOnly && (
          <Box width="100%">
            <NotificationPolicyComplianceAlert
              onMatchAndUpdateAll={handleMatchAndUpdateAll}
              hasNonCompliance={hasAnyNonCompliance}
              isLoading={isUpdatingCompliance}
            />
          </Box>
        )}

        {/* Show compliance warning if policy exists and user is not compliant (read-only mode) */}
        {policy && hasAnyNonCompliance && readOnly && (
          <ComplianceWarning variant="warning" showPolicyLink={true} />
        )}
      </Flex>

      <Flex flexDir="column" gap="4">
        <Card
          flexDir={{ base: 'column', md: 'row' }}
          bgColor="surface.primary"
          border="1px"
          borderColor="stroke.primary"
          width="100%"
        >
          <Flex
            alignItems="flex-start"
            borderRight={{ base: 'none', md: '1px solid' }}
            borderBottom={{ base: '1px solid', md: 'none' }}
            borderColor="stroke.primary"
            minWidth={{ base: '100%', md: '200px' }}
            flexShrink={0}
          >
            <Flex
              flexDir="row"
              alignItems="center"
              mt="2"
              ml="2"
              mb={{ base: '2', md: '0' }}
              gap="1"
            >
              <Icon
                as={Fire}
                size={24}
                color="content.feedback.error.primary"
              />
              <Text textStyle="heading3Bold" color="content.primary">
                High Priority
              </Text>
            </Flex>
          </Flex>

          <Flex flex={1} width="100%">
            <SignalsNotificationsForm
              notificationPriority="HIGH"
              destinations={destinations}
              readOnly={readOnly}
              refetchCompliance={handleNotificationChange}
            />
          </Flex>
        </Card>
        <Flex flexDir="column" gap="2">
          <Card
            flexDir={{ base: 'column', md: 'row' }}
            bgColor="surface.primary"
            border="1px"
            borderColor="stroke.primary"
            width="100%"
          >
            <Flex
              alignItems="flex-start"
              borderRight={{ base: 'none', md: '1px solid' }}
              borderBottom={{ base: '1px solid', md: 'none' }}
              borderColor="stroke.primary"
              minWidth={{ base: '100%', md: '200px' }}
              flexShrink={0}
            >
              <Flex
                flexDir="row"
                alignItems="center"
                mt="2"
                ml="2"
                mb={{ base: '2', md: '0' }}
                gap="1"
              >
                <Icon
                  as={Fire}
                  size={24}
                  color="content.feedback.critical.secondary"
                />
                <Text textStyle="heading3Bold" color="content.primary">
                  Medium Priority
                </Text>
              </Flex>
            </Flex>

            <Flex flex={1} width="100%">
              <SignalsNotificationsForm
                notificationPriority="MEDIUM"
                destinations={destinations}
                readOnly={readOnly}
                refetchCompliance={handleNotificationChange}
              />
            </Flex>
          </Card>
        </Flex>
        <Flex flexDir="column" gap="2">
          <Card
            flexDir={{ base: 'column', md: 'row' }}
            bgColor="surface.primary"
            border="1px"
            borderColor="stroke.primary"
            width="100%"
          >
            <Flex
              alignItems="flex-start"
              borderRight={{ base: 'none', md: '1px solid' }}
              borderBottom={{ base: '1px solid', md: 'none' }}
              borderColor="stroke.primary"
              minWidth={{ base: '100%', md: '200px' }}
              flexShrink={0}
            >
              <Flex
                flexDir="row"
                alignItems="center"
                mt="2"
                ml="2"
                mb={{ base: '2', md: '0' }}
                gap="1"
              >
                <Icon
                  as={Fire}
                  size={24}
                  color="content.feedback.critical.secondary"
                />
                <Text textStyle="heading3Bold" color="content.primary">
                  Low Priority
                </Text>
              </Flex>
            </Flex>

            <Flex flex={1} width="100%">
              <SignalsNotificationsForm
                notificationPriority="LOW"
                destinations={destinations}
                readOnly={readOnly}
                refetchCompliance={handleNotificationChange}
              />
            </Flex>
          </Card>
        </Flex>
      </Flex>
    </>
  );
};

export default SignalsNotificationSettings;

PhonePromptMessage.propTypes = {
  refetchDestinations: PropTypes.func.isRequired,
};

SignalsNotificationSettings.propTypes = {
  readOnly: PropTypes.bool,
  notificationPolicyComplianceStatus: PropTypes.arrayOf(
    PropTypes.shape({
      is_compliant: PropTypes.bool.isRequired,
      notification_policy_item_id: PropTypes.string.isRequired,
    }),
  ),
};
